import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import usePagination from "../../../hooks/usePagination";
import productActions from "../../../store/actions/items/item.actions";
import TableElementComponent from "../../../components/general/elements/TableElementComponent";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import TextInputElementComponent from "../../../components/general/elements/inputElements/TextInputElementComponent";
import "../../../styles/pages/dashboard/adminManageItemPageComponent.scss";
import "../../../styles/components/general/elements/table/customTableDesign.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SkeletonTable } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const AdminManageItemPageComponent = ({ productActions }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(""); // User's input value
  const [searchQuery, setSearchQuery] = useState(""); // Actual query sent to API

  const {
    items: products,
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch,
  } = usePagination({
    queryKey: ["admin-items", searchQuery], // Only updates when searchQuery changes
    fetchFunction: async (pageParam) => {
      const params = {
        pageNumber: pageParam,
        pageSize: 10,
        searchTerm: searchQuery, // Use searchQuery instead of searchTerm
      };
      const response = await productActions.getItems(params);
      return response;
    },
  });

  useEffect(() => {
    if (!searchParams.get("d")) {
      refetch(); // ✅ Refetch data when the dialog is closed
    }
  }, [searchParams]);

  // 🔍 Handle search when the button is clicked
  const handleSearch = (event) => {
    const { value } = event.target;
    setSearchQuery(value); // Update the query state → triggers fetch
  };

  const showSkeleton = useDelayedFlag(isLoading);

  const handleActionClick = (action, itemId) => {
    navigate(`?d=itemExtension&action=${action}&itemId=${itemId}`);
  };

  const getItemsTable = () => {
    const columns = [
      {
        label: "תמונה",
        field: "pictureUrl",
        render: (value, row) => (
          <div className="imageColumnContainer">
            <img src={value || "https://via.placeholder.com/50"} alt={row.itemDescription} />
          </div>
        ),
      },
      { label: "קוד פריט", field: "itemId" },
      { label: "שם פריט", field: "itemDescription" },
      { label: 'מק"ט', field: "catalogNumber" },
      { label: "קבוצת פריטים", field: "itemGroup.itemGroupName", render: (value, row) => <span>{row.itemGroup.itemGroupName}</span> },
      { label: "מחיר (₪)", field: "salePriceInNIS" },
      { label: "סדר תצוגה", field: "displayOrder" },
      { label: "זמין באתר", field: "isAvailable", render: (value, row) => <span>{row.isAvailable ? "כן" : "לא"}</span> },
      { label: "שינוי במחיר", field: "isAvailable", render: (value, row) => <span>{row.isPriceChanged ? "כן" : "לא"}</span> },
    ];

    const actions = [
      {
        label: "עריכה",
        icon: "fa-solid fa-edit",
        className: "greenButton",
        onClick: (item) => handleActionClick("edit", item.itemId),
      },
      {
        label: "מאפייני פריט",
        icon: "fa-regular fa-keyboard",
        className: "greenButton",
        onClick: (item) => handleActionClick("attributes", item.itemId),
      },
      {
        label: "פריטים מקושרים",
        icon: "fa-solid fa-network-wired",
        className: "greenButton",
        onClick: (item) => handleActionClick("relatedItems", item.itemId),
      },
    ];

    return <TableElementComponent columns={columns} data={products} actions={actions} />;
  };

  const getNoItemsElement = () => (
    <div className="noItemsContainer">
      <h2>לא נמצאו פריטים</h2>
      <p>נסה לשנות את הקריטריונים לחיפוש או לבדוק מוצרים קיימים.</p>
    </div>
  );

  const getSearchInputElement = () => {
    const inputProps = {
      ...textInputModel,
      name: "searchInput",
      value: searchQuery,
      placeholder: "חפש מוצר",
      onChange: handleSearch,
      icon: <i className="fa-solid fa-magnifying-glass"></i>,
    };

    return <TextInputElementComponent props={inputProps} />;
  };

  return (
    <div className="adminItemListContainer">
      <h1>ניהול מוצרים</h1>
      <div className="searchContainer">
        {getSearchInputElement()}
        {/* <button onClick={handleSearch} className="primaryButton">
          חפש
        </button> */}
      </div>

      {isLoading ? (
        showSkeleton && (
          <div className="productListContainer">
            <SkeletonTable rows={10} cols={8} />
          </div>
        )
      ) : products?.length > 0 ? (
        <InfiniteScroll dataLength={products.length} next={fetchNextPage} hasMore={hasNextPage} loader={<div>טוען פריטים נוספים...</div>}>
          <div className="productListContainer">{getItemsTable()}</div>
        </InfiniteScroll>
      ) : (
        getNoItemsElement()
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  productReducer: state.productReducer, // Access product state
});

const mapDispatchToProps = (dispatch) => ({
  productActions: bindActionCreators(productActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminManageItemPageComponent);
